import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { DateRangePickerInput } from '@creditornot/cb-date-picker';
import { Download } from '@creditornot/cb-icons';
import { cssThemedValue, Spinner } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Empty } from '@creditornot/cb-placeholders';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';
import { getErrorContent, localize } from 'common/utils';
import { useCountryConfigContext } from 'modules/country/CountryConfigContext';
import { useDownloadBrandPurchasedDataV2 } from 'modules/enterprise/hooks/useDownloadBrandPurchasedData';

import Table from './Table';
import { useEnterpriseContext } from '../../context/EnterpriseContext';
import useDatePeriod from '../../hooks/useDatePeriod';
import useGetDefaultVenueIds from '../../hooks/useGetDefaultVenueIds';
import useGetEnterpriseVenues from '../../hooks/useGetEnterpriseVenues';
import { PreDefinedPeriod } from '../../types';

const ErrorAlert = styled(Alert).attrs({ variant: 'error' })`
  margin: 24px;
`;

const Root = styled.div`
  background-color: ${cssThemedValue('surfaceMain')};
  color: ${cssThemedValue('textPrimary')};
  display: flex;
  width: 100%;
  height: 100%;
`;

const Sidebar = styled.div`
  flex-shrink: 0;
  width: 320px;
  padding: 24px;
  border-inline-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const DateRangeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-block-start: 16px;
  margin-inline-start: -8px;
  margin-inline-end: -8px;
`;

const RightContent = styled.div`
  overflow: auto;
  width: 100%;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 72px;
  padding: 24px;
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
`;

const Wrapper = styled.div`
  margin-block-start: 32px;
  flex: 1;
`;

const periodTranslationMap: Record<PreDefinedPeriod, string> = {
  yesterday: 'purchases.labels.yesterday',
  'this-week': 'purchases.labels.this-week',
  'last-week': 'purchases.labels.last-week',
  'this-month': 'purchases.labels.this-month',
  'last-month': 'purchases.labels.last-month',
};

const LoadingSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding-block-start: 40px;
  padding-block-end: 40px;
`;

const Loading = () => (
  <LoadingSpinnerContainer>
    <Spinner size="large" />
  </LoadingSpinnerContainer>
);

const EnterprisePurchaseData = () => {
  const intl = useIntl();

  const { enterprise } = useEnterpriseContext();
  const { brandsVenues, error } = useGetEnterpriseVenues();
  const { brandSelectedVenueIds, setBrandSelectedVenueIds } = useGetDefaultVenueIds(brandsVenues);
  const countryConfig = useCountryConfigContext();

  const { downloadPeriod, dateRange, setDateRangeQuery } = useDatePeriod();
  const enterpriseName = useMemo(() => {
    return localize(
      [{ lang: countryConfig?.primary_language as string, value: enterprise?.name as string }],
      intl.locale,
    );
  }, [countryConfig?.primary_language, enterprise?.name, intl.locale]);
  const enterpriseId = enterprise?.id ?? '';

  const selectedVenueIds = useMemo(() => {
    return Object.keys(brandSelectedVenueIds).reduce((previousValue, currentKey) => {
      return [...previousValue, ...brandSelectedVenueIds[currentKey]];
    }, []);
  }, [brandSelectedVenueIds]);

  // Currently, we get timezone from one of the venues under an Enterprise,
  // assuming all the venues are under the same timezone.
  // In the future, we may need to go over this logic as an enterprise can have venues with different timezones.
  const venues = brandsVenues?.length ? brandsVenues[0].venues : [];
  const timezone = !!venues && venues?.length > 0 ? venues[0].timezone : '';

  const { downloadPurchaseDataV2, isDownloadingV2 } = useDownloadBrandPurchasedDataV2(
    selectedVenueIds,
    enterpriseId,
    enterpriseName,
    'enterprise',
    timezone,
  );

  if (error) {
    return (
      <Root>
        <Wrapper>
          <ErrorAlert>{getErrorContent(error)}</ErrorAlert>
        </Wrapper>
      </Root>
    );
  }

  if (!brandsVenues) {
    return (
      <Root>
        <Wrapper>
          <Loading />
        </Wrapper>
      </Root>
    );
  }

  if (!brandsVenues.length) {
    return (
      <Root>
        <Wrapper>
          <Empty
            title={intl.formatMessage({ id: 'purchases.merchant.empty-title' })}
            description={intl.formatMessage({ id: 'purchases.merchant.empty-description' })}
          />
        </Wrapper>
      </Root>
    );
  }

  const handleSelectRows = (brandId: string) => {
    return (selectedIds: string[]) => {
      const cloneBrandSelectedVenueIds = { ...brandSelectedVenueIds };
      cloneBrandSelectedVenueIds[brandId] = selectedIds;
      setBrandSelectedVenueIds(cloneBrandSelectedVenueIds);
    };
  };

  const lastThreeMonths = DateTime.now().minus({ months: 3 }).plus({ days: 1 }).toJSDate();
  const today = DateTime.now().toJSDate();

  const disabledDays = {
    before: lastThreeMonths,
    after: today,
  };

  return (
    <Root>
      <Sidebar>
        <MenuItemGroup>
          {Object.keys(periodTranslationMap).map((period: PreDefinedPeriod) => (
            <MenuActionItem
              key={period}
              variant="black"
              onClick={() => {
                setDateRangeQuery({ period, from: null, to: null });
              }}
              selected={downloadPeriod === period}
              disabled={isDownloadingV2}
            >
              <FormattedMessage id={periodTranslationMap[period]} />
            </MenuActionItem>
          ))}
        </MenuItemGroup>
        <DateRangeWrapper>
          <DateRangePickerInput
            value={dateRange}
            onChange={dateRange => {
              if (dateRange) {
                setDateRangeQuery({ period: 'custom', ...dateRange });
              }
            }}
            disabled={isDownloadingV2}
            disableClear
            disabledDays={disabledDays}
          />
        </DateRangeWrapper>
      </Sidebar>
      <RightContent>
        <Toolbar>
          <T.Heading6>
            <FormattedMessage id="purchases.enterprise.combined-data-header" />
          </T.Heading6>
          <Button
            variant="blue"
            size="small"
            icon={<Download />}
            disabled={isDownloadingV2 || selectedVenueIds.length === 0}
            loading={isDownloadingV2}
            onClick={() => downloadPurchaseDataV2()}
          >
            <FormattedMessage id="purchases.merchant.download-report" values={{ count: selectedVenueIds.length }} />
          </Button>
        </Toolbar>

        {brandsVenues.map(brandVenues => {
          const { brand, venues } = brandVenues;
          const selectedVenueIds = brandSelectedVenueIds[brand.id];
          return (
            <Table
              key={brand.id}
              brandName={brand.name}
              venues={venues}
              selectedVenueIds={selectedVenueIds}
              onSelectRows={handleSelectRows(brand.id)}
              isDownloading={isDownloadingV2}
            />
          );
        })}
      </RightContent>
    </Root>
  );
};

export default EnterprisePurchaseData;
